// Top Projects board tiers — the business's priority groupings from the
// exec prioritization deck. ONE definition drives the board page, the
// Executive Gantt's Top 10 view, and the priority chips, so a tier
// rename or re-order happens in exactly one place.
//
// Tier is orthogonal to lifecycle status: a project can be Top 10 while
// still pre-approval. NULL priority_tier = not on the board.

export type PriorityTier = "top10" | "just_do" | "next_up" | "unscoped" | "paused";

export interface TierMeta {
  key: PriorityTier;
  label: string;
  /** One-line description shown under the band header on /top-projects. */
  blurb: string;
  /** Chip styling (background + text). */
  chipClass: string;
  /** Band accents — same vocabulary as the roadmap's QUARTER_ACCENT:
   *  a hex for the card's left edge bar (painted as inset box-shadow)
   *  plus tinted header/count-chip classes. */
  barHex: string;
  headerBg: string;
  countChip: string;
}

/** Display order — same order as the deck's summary slide. */
export const TIER_ORDER: TierMeta[] = [
  {
    key: "top10",
    label: "Top 10",
    blurb:
      "Projects with estimated economic benefit — revenue and/or cost reduction the business can put a number on.",
    chipClass: "bg-brand-navy-600 text-white",
    barHex: "#818cf8",
    headerBg: "bg-brand-navy-50 dark:bg-brand-navy-500/10",
    countChip: "bg-brand-navy-100 dark:bg-brand-navy-500/15 text-brand-navy-700 dark:text-brand-navy-300",
  },
  {
    key: "just_do",
    label: "Just Do Its",
    blurb:
      "Low IT cost, no 2026 EBITDA estimate needed — several close to completion. Freed resources land here first.",
    chipClass: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300",
    barHex: "#34d399",
    headerBg: "bg-emerald-50 dark:bg-emerald-500/10",
    countChip: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  {
    key: "next_up",
    label: "Next up",
    blurb:
      "Next projects for IT to prioritize as resources allow, following the standard approval process.",
    chipClass: "bg-sky-100 dark:bg-sky-500/15 text-sky-800 dark:text-sky-300",
    barHex: "#38bdf8",
    headerBg: "bg-sky-50 dark:bg-sky-500/10",
    countChip: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",
  },
  {
    key: "unscoped",
    label: "New / unscoped",
    blurb: "Every new request starts here. Approve → Next up; Decline → off the board.",
    chipClass: "bg-muted text-ink-2",
    barHex: "#cbd5e1",
    headerBg: "bg-canvas",
    countChip: "bg-raised text-ink-3",
  },
  {
    key: "paused",
    label: "Paused",
    blurb: "On hold pending the process-mapping pilot (follow-up week of 9/14).",
    chipClass: "bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300",
    barHex: "#fbbf24",
    headerBg: "bg-amber-50 dark:bg-amber-500/10",
    countChip: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
  },
];

export const TIER_META: Record<string, TierMeta> = Object.fromEntries(
  TIER_ORDER.map((t) => [t.key, t]),
);

/** Chip text for a project: "Top 10 · #3" for ranked Top-10 rows, the
 *  bare tier label otherwise. Null when the project isn't on the board. */
export function tierChipText(p: {
  priority_tier?: string | null;
  priority_rank?: number | null;
}): string | null {
  const meta = p.priority_tier ? TIER_META[p.priority_tier] : undefined;
  if (!meta) return null;
  if (meta.key === "top10" && p.priority_rank != null) {
    return `${meta.label} · #${Math.round(p.priority_rank)}`;
  }
  return meta.label;
}

/** Sort comparator within a tier: rank ascending, un-ranked last, name ties. */
export function byRank(
  a: { priority_rank?: number | null; name?: string | null },
  b: { priority_rank?: number | null; name?: string | null },
): number {
  const ar = a.priority_rank ?? Infinity;
  const br = b.priority_rank ?? Infinity;
  if (ar !== br) return ar - br;
  return (a.name ?? "").localeCompare(b.name ?? "");
}
